import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
    CronType,
    CustomCron,
} from '@components/ui-cron/cron.models';
import { TranslateService } from '@ngx-translate/core';
import { unsubscribe } from '@utils/object.utils';
import UiValidators from '@validators';

import { Subscription } from 'rxjs';

const DAYS = [
    { id: 'MON', text: 'monday' },
    { id: 'TUE', text: 'tuesday' },
    { id: 'WED', text: 'wednesday' },
    { id: 'THU', text: 'thursday' },
    { id: 'FRI', text: 'friday' },
    { id: 'SAT', text: 'saturday' },
    { id: 'SUN', text: 'sunday' },
];

// TODO: (VJ) Rewrite this piece of garbage; (AG) +2
@Component({
    selector: 'ui-cron',
    templateUrl: 'ui-cron.component.html',
    styleUrls: ['ui-cron.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UiCronComponent),
        multi: true,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiCronComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() public form!: FormGroup;
    @Input() public timezone!: string;
    @Input() public model!: CustomCron;
    public minutes = ['1', '5', '10', '15', '30'].map((entry) =>
        ({
            id: entry,
            text: entry,
        }),
    );
    public selectedMinute!: any[];

    public days: Array<any> = DAYS.map(day => ({
        id: day.id,
        weekly: this._translateService.instant(`cron_weekly_${day.text}`),
        monthly: this._translateService.instant(`cron_monthly_${day.text}`),
    }));

    private _valueChangesSub!: Subscription;

    public constructor(
        private _cd: ChangeDetectorRef,
        private _translateService: TranslateService,
    ) { }

    public onMinuteValueChanged(item: any) {
        this.model.minutely.atMinute = !item.id ? null : parseInt(item.id, 10);
    }

    public typeIs(typeStr: string): boolean {
        return (<any>CronType)[typeStr] === this.model.type;
    }

    public updateCronType(type: string) {
        const newType = (<any>CronType)[type];
        this.model.type = newType;
        // AG: aaargh!
        if (this.model.type === CronType.Minutely) {
            const minutes = this.form.get('minutelyAtMinute')!.value;
            this.model.minutely.atMinute = minutes[0] && minutes[0].id ? parseInt(minutes[0].id, 10) : null;
        }

        this._onChange(this.model);
        this._setCronConditionalValidation();
        this.form.updateValueAndValidity();
        this.form.markAsDirty();
    }

    public onDayChange(event: MatCheckboxChange, tab: string, weekdays: Array<any>, day: any) {

        this.updateCronType(tab);

        if (event.checked) {
            weekdays.push(day);
        } else {
            let index = -1;

            for (let i = 0; i < weekdays.length; i++) {
                if (weekdays[i].id === day.id) {
                    index = i;
                    break;
                }
            }

            if (index > -1) {
                weekdays.splice(index, 1);
            }
        }
        this._onChange(this.model);
        this.form.updateValueAndValidity();
        this.form.markAsDirty();
    }

    public isWeekdayChecked(list: Array<any>, day: any): boolean {
        return list && list.filter(d => d.id === day.id).length > 0;
    }

    public onClick() {
        this._onTouch();
    }

    ngOnInit() {
        const isFormPristine = this.form.pristine;
        this.form.setValidators(this._weekDayFormValidator);
        const atMinute = this.model.minutely.atMinute;
        if (atMinute) {
            this.selectedMinute = [
                this.minutes
                    .find(m => m.id === atMinute.toString()) ||
                this.minutes[0],
            ];
        } else {
            this.selectedMinute = [this.minutes[0]];
        }
        this._cd.detectChanges();
        // TODO: refactor this. Something causes the main form to read as dirty (AB)
        if (isFormPristine) {
            this.form.markAsPristine();
        }
        this._valueChangesSub = this.form.valueChanges.subscribe(value => {
            const model = this.model;
            model.hourly.frequencyInHours = value.hourlyEveryHours;
            model.hourly.atMinute = value.hourlyAtMinute;
            model.daily.atHour = value.dailyAtHour;
            model.daily.atMinute = value.dailyAtMinute;
            model.weekly.atHour = value.weeklyAtHour;
            model.weekly.atMinute = value.weeklyAtMinute;
            model.monthly.frequencyInMonths = value.monthlyFrequency;
            model.monthly.atHour = value.monthlyAtHour;
            model.monthly.atMinute = value.monthlyAtMinute;
            model.advancedCronExpression = value.advancedCron;
        });
    }

    ngOnDestroy() {
        unsubscribe(
            this._valueChangesSub,
        );
    }

    registerOnChange(fn: Function): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this._onTouch = fn;
    }

    writeValue(value: any) {
        if (value) {
            this.model = value;
        }
    }

    get value(): any {
        return this.model;
    }

    set value(v: any) {
        if (v !== this.model) {
            this.model = v;
            this._onChange(v);
        }
    }

    private _onChange: Function = () => { };
    private _onTouch: Function = () => { };


    // TO-DO: Investigate if this is necessary
    // the form inputs are already decorated with these validation attributes
    private _setCronConditionalValidation() {
        this._updateCronValidator('minutelyAtMinute',
            CronType.Minutely,
            Validators.compose([
                UiValidators.required(),
            ]),
        );

        this._updateCronValidator('hourlyEveryHours',
            CronType.Hourly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(1, 23),
            ]),
        );
        this._updateCronValidator('hourlyAtMinute',
            CronType.Hourly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 59),
            ]),
        );

        this._updateCronValidator('dailyAtHour',
            CronType.Daily,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 23),
            ]),
        );
        this._updateCronValidator('dailyAtMinute',
            CronType.Daily,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 59),
            ]),
        );

        this._updateCronValidator('weeklyAtHour',
            CronType.Weekly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 23),
            ]),
        );
        this._updateCronValidator('weeklyAtMinute',
            CronType.Weekly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 59),
            ]),
        );

        this._updateCronValidator('monthlyAtHour',
            CronType.Monthly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 23),
            ]),
        );
        this._updateCronValidator('monthlyAtMinute',
            CronType.Monthly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(0, 59),
            ]),
        );
        this._updateCronValidator('monthlyFrequency',
            CronType.Monthly,
            Validators.compose([
                UiValidators.required(),
                UiValidators.range(1, 12),
            ]),
        );

        this._updateCronValidator('advancedCron',
            CronType.Advanced, Validators.required);
    }

    private _updateCronValidator(subGroupName: string, cronType: CronType, validator: any) {
        const subgroup = this.form.controls[subGroupName];
        subgroup.setValidators(this.model.type === cronType ? validator : null);
        subgroup.updateValueAndValidity();
    }

    private _weekDayFormValidator = (): ValidationErrors | null => {
        const cronType = this.model.type;

        if ([CronType.Weekly, CronType.Monthly].indexOf(cronType) === -1) {
            return null;
        }

        const weekOrMonth =
            cronType === CronType.Weekly ?
                this.model.weekly :
                this.model.monthly;

        if (weekOrMonth.weekdays.length !== 0) {
            return null;
        }

        return { weekDay: true };
    }
}
